import {
  readAxisFilterChips,
  writeAxisFilterChips,
  type AxisFilterParamMap,
} from "@/components/filter";
import {
  ALL_SESSION_FILTER,
  clearPolicySelectionParam,
  experimentSearchParamKey,
  memberSearchParamKey,
  type SessionScope,
} from "@/modules/training/public";
import {
  POLICY_FILTER_FIELDS,
  type PolicyFilterChip,
  type PolicyFilterField,
} from "./policyFilterFields";
import {
  ALL_POLICY_REGISTERED_WITHIN,
  isPolicyRegisteredWithin,
  type PolicyRegisteredWithin,
} from "./policyRegisteredWithin";

// Policy 카탈로그의 member/experiment 필터·등록일 기간·정책 선택을 URL query param 에 보존하는
// 순수 helper. 그래서 탭 이동 후 복귀, 새로고침, 딥링크 공유 뒤에도 선택이 유지된다. React 연결부
// (useSearchParams)는 호출부가 맡고, 여기에는 로직만 둔다(utils/pageParam.ts 와 같은 분리).

export const scopeSearchParamKey = "scope";
export const defaultSessionScope: SessionScope = "me";
const legacyWorkspaceSearchParamKey = "workspace";

// Policy 카탈로그 검색 칩의 쿼리 키. `policy_` 네임스페이스로 카탈로그의 member/experiment
// 드롭다운과 활성 Results 창 identity(`results_policy`)를 같은 URL 에서 구분한다.
const policyFilterParamByField: AxisFilterParamMap<PolicyFilterField> = {
  name: "policy_name",
  experiment: "policy_experiment",
  branch: "policy_branch",
  dataset: "policy_dataset",
  owner: "policy_owner",
};

export function readPolicyFilterChips(params: URLSearchParams): PolicyFilterChip[] {
  return readAxisFilterChips(params, POLICY_FILTER_FIELDS, policyFilterParamByField);
}

export function writePolicyFilterChips(
  params: URLSearchParams,
  chips: readonly PolicyFilterChip[],
): URLSearchParams {
  return writeAxisFilterChips(params, POLICY_FILTER_FIELDS, policyFilterParamByField, chips);
}

// 등록일 기간 퀵 필터의 쿼리 키. 검색 칩과 같은 `policy_` 접두사를 써서 Results 창 키와 충돌하지
// 않는다. 검색 칩과 같이 스코프에 종속되지 않는다. 그래서 스코프·member 캐스케이드
// (writeScopeParam·writeMemberParam)가 지우지 않는다. 어느 스코프에서도 뜻이 같다.
export const policyRegisteredWithinSearchParamKey = "policy_registered";

// URL 은 외부 입력이다. 키가 없거나 모르는 값이면 전체(all)로 읽는다.
export function readPolicyRegisteredWithin(params: URLSearchParams): PolicyRegisteredWithin {
  const value = params.get(policyRegisteredWithinSearchParamKey);
  return isPolicyRegisteredWithin(value) ? value : ALL_POLICY_REGISTERED_WITHIN;
}

// 컨트롤이 준 string 을 그대로 받는다(writeScopeParam 과 같은 계약). 어휘 밖 값과 기본값(all)은
// 키를 빼서 정리된 URL 을 유지한다. 입력 params 는 변경하지 않는다.
export function writePolicyRegisteredWithinParam(
  params: URLSearchParams,
  value: string | null,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (isPolicyRegisteredWithin(value) && value !== ALL_POLICY_REGISTERED_WITHIN) {
    next.set(policyRegisteredWithinSearchParamKey, value);
  } else {
    next.delete(policyRegisteredWithinSearchParamKey);
  }
  return next;
}

// 스코프를 기록한다. 부모 키를 쓰는 커밋은 자식 키를 지운다(cascade 규칙). 그래서 스코프에 종속인
// member/experiment 필터 키와 정책 선택 키를 같은 커밋에서 함께 지운다.
//
// 검색 칩(policy_*)은 여기서 지우지 않는다. 지우는 기준은 "스코프가 바뀌면 계속 뜻이 없는 값"이고,
// 그런 값은 id 뿐이다. 부분 일치 문자열은 어느 스코프에서도 뜻이 있다. 지우면 결과가 알림 없이
// 넓어진다. 그래서 검색 칩은 스코프를 바꿔도 남는다. 기본 스코프(me)는 키를 빼서 정리된 URL 을
// 유지한다. Training 과 Evaluation 두 페이지의 스코프 스위처가 함께 쓴다. 필터 키를 쓰는 곳은
// Evaluation 뿐이다. 그래서 Training URL 에서는 오래된 딥링크 정리 외에는 no-op 이다.
export function writeScopeParam(params: URLSearchParams, scope: string | null): URLSearchParams {
  const next = clearPolicySelectionParam(clearPolicyFilterParams(params));
  if (scope === null || scope === defaultSessionScope) {
    next.delete(scopeSearchParamKey);
  } else {
    next.set(scopeSearchParamKey, scope);
  }
  return next;
}

// member 를 기록한다. 실험 목록과 정책 선택은 멤버 스코프에 종속이라 같은 커밋에서 지운다.
// 전체 선택은 키를 빼서 정리된 URL 을 유지한다. 입력 params 는 변경하지 않는다.
export function writeMemberParam(params: URLSearchParams, memberUuid: string): URLSearchParams {
  const next = clearPolicySelectionParam(clearPolicyFilterParams(params));
  if (memberUuid !== ALL_SESSION_FILTER) {
    next.set(memberSearchParamKey, memberUuid);
  }
  return next;
}

export function writeExperimentParam(
  params: URLSearchParams,
  experimentId: string,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (experimentId === ALL_SESSION_FILTER) {
    next.delete(experimentSearchParamKey);
  } else {
    next.set(experimentSearchParamKey, experimentId);
  }
  return next;
}

// 두 필터 키와 이전 workspace 키를 함께 지운다. 다음 경우가 함께 쓴다: scope 전환·group 변경처럼
// 필터의 부모 맥락이 바뀌는 커밋, 목록에 없는 member 딥링크 정리.
export function clearPolicyFilterParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(memberSearchParamKey);
  next.delete(experimentSearchParamKey);
  next.delete(legacyWorkspaceSearchParamKey);
  return next;
}

// 이전 ?workspace= 딥링크가 새 member/experiment 선택까지 지우지 않도록 그 키만 정리한다.
export function clearLegacyWorkspaceParam(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(legacyWorkspaceSearchParamKey);
  return next;
}

export function hasLegacyWorkspaceParam(params: URLSearchParams): boolean {
  return params.has(legacyWorkspaceSearchParamKey);
}
